import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { attachmentApi } from '@core/api';
import type { Attachment } from '@core/types/attachment';
import { ResourceCard } from './ResourceCard';
import type { ResourceGroup, ResourceCardData } from '@/features/resources/types';

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function ChevronIcon({ className, open }: { className?: string; open: boolean }) {
  return (
    <svg className={`${className} transition-transform ${open ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

interface ResourceListProps {
  groups: ResourceGroup[];
  isLoading: boolean;
  onUnlink: (resourceId: string) => void;
  onNavigate: (data: ResourceCardData) => void;
  onAddClick: () => void;
  /** Opens the fuller link-management panel (direction filter, stats, edit
   * relation). Optional so existing callers keep compiling unchanged. */
  onManageClick?: () => void;
}

export function ResourceList({ groups, isLoading, onUnlink, onNavigate, onAddClick, onManageClick }: ResourceListProps) {
  const { t } = useI18n();
  const [files, setFiles] = useState<Record<string, Attachment>>({});
  const fileIds = groups.flatMap((group) => group.items.filter((item) => item.isAttachment).map((item) => item.id)).sort().join('|');
  useEffect(() => {
    setFiles({});
    if (!fileIds) return;
    let active = true;
    attachmentApi.ListAttachmentsByIDs({ ids: fileIds.split('|') }).then((response) => {
      if (active) setFiles(Object.fromEntries((response.attachments?.attachments || []).map((file) => [file.entity?.id || '', file])));
    }).catch(() => { /* The resource row remains usable when metadata cannot be read. */ });
    return () => { active = false; };
  }, [fileIds]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const set = new Set<string>();
    groups.forEach((g) => { if (g.groupOrder < 5) set.add(g.relationType); });
    return set;
  });
  const [showAllMap, setShowAllMap] = useState<Set<string>>(new Set());

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleShowAll = (key: string) => {
    setShowAllMap((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const nonEmptyGroups = groups.filter((g) => g.items.length > 0);
  const totalItems = groups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-rail-foreground">
          {t('layout.rail.resources')}
          {totalItems > 0 && (
            <span className="ml-1.5 text-xs text-muted-foreground">({totalItems})</span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {onManageClick && totalItems > 0 && (
            <button
              onClick={onManageClick}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('rail.resources.manage')}
            </button>
          )}
          <button
            onClick={onAddClick}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            {t('rail.resources.addResource')}
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse flex items-center gap-2 px-2 py-1.5">
              <div className="h-4 w-4 bg-muted rounded" />
              <div className="flex-1 space-y-1">
                <div className="h-3.5 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && nonEmptyGroups.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <svg className="h-8 w-8 text-muted-foreground/50 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
          </svg>
          <p className="text-sm text-muted-foreground">{t('layout.rail.resourcesEmpty')}</p>
          <button
            onClick={onAddClick}
            className="mt-2 text-xs text-primary hover:underline"
          >
            {t('rail.resources.addResource')}
          </button>
        </div>
      )}

      {!isLoading && nonEmptyGroups.map((group) => {
        const isOpen = expandedGroups.has(group.relationType);
        const showAll = showAllMap.has(group.relationType);
        const visibleItems = showAll ? group.items : group.items.slice(0, 5);
        const remaining = group.items.length - 5;

        return (
          <div key={group.relationType} className="space-y-0.5">
            <button
              onClick={() => toggleGroup(group.relationType)}
              className="flex items-center gap-1.5 w-full text-left px-1 py-1 rounded-md hover:bg-accent/30 transition-colors"
            >
              <ChevronIcon className="h-3.5 w-3.5 text-muted-foreground" open={isOpen} />
              <svg className="h-3.5 w-3.5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d={group.icon} />
              </svg>
              <span className="text-xs font-medium text-muted-foreground">{t(group.labelKey)}</span>
              <span className="text-xs text-muted-foreground/70">{group.items.length}</span>
            </button>
            {isOpen && (
              <div className="ml-1">
                {visibleItems.map((item) => (
                  <ResourceCard
                    key={item.id}
                    data={{ ...item, title: files[item.id]?.file_name || item.title }}
                    onUnlink={onUnlink}
                    onNavigate={onNavigate}
                    borderColor={group.borderColor}
                  />
                ))}
                {remaining > 0 && !showAll && (
                  <button
                    onClick={() => toggleShowAll(group.relationType)}
                    className="w-full text-xs text-primary hover:underline py-1 text-center"
                  >
                    {t('rail.resources.showMore', { count: remaining })}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
